
'use client';

import { usePathname } from 'next/navigation';
import { Bell, Home, MessageCircle, PlaySquare, Store, FileText, ChevronDown, Palette, LayoutGrid, Menu, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import Image from 'next/image';
import { Skeleton } from './ui/skeleton';
import { SearchArtists } from './search-artists';
import { FriendProvider } from '@/context/FriendContext';
import { CategoryProvider, useCategory } from '@/context/CategoryContext';
import Link from 'next/link';
import { useSettings } from '@/context/SettingsContext';

interface HeaderProps {
  onLeftSidebarToggle?: () => void;
  onRightSidebarToggle?: () => void;
}


function HeaderContent({ onLeftSidebarToggle, onRightSidebarToggle }: HeaderProps) {
  const pathname = usePathname();
  const { user, logOut } = useAuth();
  const { theme, setTheme, themes, loading: themeLoading } = useTheme();
  const { categories } = useCategory();
  const { globalLogoUrl, loading: settingsLoading } = useSettings();

  const navLinks = [
    { href: '/', icon: Home, label: 'Home' },
    { href: '/cv', icon: FileText, label: 'CV' },
    { href: '/marketplace', icon: Store, label: 'Marketplace' },
    { href: '/gaming', icon: PlaySquare, label: 'Gaming' },
  ];
  
  const effectiveLogoUrl = theme.id === 'default' ? globalLogoUrl : theme.logoUrl;

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-card border-b shadow-sm h-14">
      <div className="container mx-auto flex items-center h-full px-4">
        <div className="flex items-center gap-2">
           <Button variant="ghost" size="icon" className="lg:hidden" onClick={onLeftSidebarToggle}>
             <Menu className="h-6 w-6" />
           </Button>
           <a href="/" aria-label="Home">
            {themeLoading || settingsLoading ? (
                <Skeleton className="h-10 w-10 rounded-full" />
            ) : (
                <Image src={effectiveLogoUrl} alt="App Logo" width={40} height={40} />
            )}
          </a>
          <SearchArtists />
        </div>

        <nav className="hidden lg:flex flex-1 justify-center items-center h-full">
          <ul className="flex items-center h-full">
            {navLinks.map(({ href, icon: Icon, label }) => (
              <li key={href} className="h-full">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a href={href} className={cn('flex items-center justify-center h-full w-28 text-muted-foreground hover:bg-secondary transition-colors relative', pathname === href && 'text-primary')} aria-label={label}>
                        <Icon className="h-6 w-6" />
                        {pathname === href && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary"></div>
                        )}
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{label}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-2 ml-auto">
          <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="icon" className="rounded-full">
                    <Palette className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Choose a theme</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={theme.id} onValueChange={(value) => setTheme(themes.find(t => t.id === value) || themes[0])}>
                    {themes.map(t => (
                        <DropdownMenuRadioItem key={t.id} value={t.id}>
                           {t.name}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="icon" className="rounded-full">
                    <LayoutGrid className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Categories</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {categories.map(category => (
                   <DropdownMenuItem key={category.id} asChild>
                      <Link href={`/category/${category.slug}`}>{category.name}</Link>
                   </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="secondary" size="icon" className="rounded-full">
            <MessageCircle className="h-5 w-5" />
          </Button>
          <Button variant="secondary" size="icon" className="rounded-full">
            <Bell className="h-5 w-5" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 rounded-full p-1 h-auto">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.photoURL || "https://placehold.co/40x40.png"} alt={user?.displayName || "User"} data-ai-hint="person portrait" />
                  <AvatarFallback>{user?.displayName?.split(' ').map(n=>n[0]).join('') || 'U'}</AvatarFallback>
                </Avatar>
                <ChevronDown className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.displayName || 'My Account'}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user && (
                <DropdownMenuItem asChild>
                  <a href={`/friends/${user.uid}`}>Profile</a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <a href="/settings">Settings</a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logOut}>
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={onRightSidebarToggle}>
             <Users className="h-6 w-6" />
           </Button>
        </div>
      </div>
    </header>
  );
}


export function Header({ onLeftSidebarToggle, onRightSidebarToggle }: HeaderProps) {
  return (
    <FriendProvider>
        <CategoryProvider>
            <HeaderContent 
            onLeftSidebarToggle={onLeftSidebarToggle}
            onRightSidebarToggle={onRightSidebarToggle}
            />
        </CategoryProvider>
    </FriendProvider>
  )
}
